<template>
  <div id="edit-image-component">
    <div id="image-size-wrap">
      <div class="title">Image Size</div>
      <div id="inputs-wrap">
        <div class="input-container">
          <label for="width-input">Width</label>
          <div style="display: flex">
            <input id="width-input" class="input-left" v-model="imageWidth" @input="handleWidth" />
            <input class="input-right" name="right-input" placeholder="in." />
          </div>
        </div>
        <div class="input-container">
          <label for="height-input">Height</label>
          <div style="display: flex">
            <input
              id="height-input"
              class="input-left"
              v-model="imageHeight"
              @input="handleHeight"
            />
            <input class="input-right" name="right-input" placeholder="in." />
          </div>
        </div>
      </div>
      <div
        :style="{
          color: resolutionWidth < 300 || resolutionHeight < 300 ? '#ff0000' : '#fff',
        }"
      >
        <div v-if="resolutionWidth === resolutionHeight">Resolution {{ resolution }} DPI</div>
        <div v-else>Resolution {{ resolutionWidth }} x {{ resolutionHeight }} DPI</div>
      </div>
      <button aria-label="Reset image size" id="reset-btn" @click="resetDPI">
        Reset Size
      </button>
    </div>
    <div id="border-wrap">
      <div class="title">Image Border</div>
      <div id="border-controls">
        <div id="color-wrap">
          <p>Color</p>
          <div id="border-circle" ref="borderElement" @click="handleToggleColorPickr"></div>
        </div>
        <div class="range-wrap">
          <p>Border Width</p>
          <input
            type="range"
            min="0"
            max="100"
            @input="handleBorderWidth"
            class="slider"
            ref="borderWidthRange"
          />
        </div>
      </div>
      <div id="styles-wrap">
        <p>Style</p>
        <div id="border-styles">
          <label for="solid"
            ><input
              @click="setBorderStyle"
              type="radio"
              name="border-style"
              id="solid"
              value="solid"
            />Solid</label
          >
          <label for="dotted"
            ><input
              @click="setBorderStyle"
              type="radio"
              name="border-style"
              id="dotted"
              value="dotted"
            />Dotted</label
          >
          <label for="dashed"
            ><input
              @click="setBorderStyle"
              type="radio"
              name="border-style"
              id="dashed"
              value="dashed"
            />Dashed</label
          >
        </div>
      </div>
    </div>
    <div id="opacity-controls">
      <div class="title">Transparency</div>
      <div id="slider-wrap">
        <div id="slider-container">
          <input
            type="range"
            min="0"
            max="100"
            @input="handleOpacity"
            class="slider"
            id="opacity-slider"
            ref="opacityRange"
          />
        </div>
        <div>{{ opacitySlider }}%</div>
      </div>
    </div>
    <button
      v-if="currentActiveObject.imageId"
      aria-label="Crop image"
      @click="$emit('reCropImage')"
    >
      Crop Image
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, nextTick, onMounted } from "vue";

import { store } from "@/store";

import { updateImage, scaledDPI } from "@/canvas-api/UserObjects/Image";

import usePickr from "@/composables/usePickr";

import { ColorPickrState } from "@/types";

export default defineComponent({
  name: "EditImage",
  emits: ["reCropImage"],
  setup() {
    const dpi = computed(() => store.getters.dpi);
    const currentActiveObject = computed(() => store.getters.currentActiveObject);
    const colorPickrState = computed<ColorPickrState>(() => store.getters.colorPickrState);

    const imageWidth = ref(0);
    const imageHeight = ref(0);
    const resolution = ref(0);
    const resolutionWidth = ref(0);
    const resolutionHeight = ref(0);

    const borderSlider = ref(0);
    const opacitySlider = ref(0);
    const borderWidthRange = ref<HTMLInputElement>();
    const opacityRange = ref<HTMLInputElement>();
    const borderElement = ref<HTMLDivElement>();
    onMounted(() => {
      calculateDimsAndRes();

      if (borderWidthRange.value) {
        borderWidthRange.value.value = (currentActiveObject.value.strokeWidth! * 10).toString();
      }

      if (opacityRange.value) {
        opacityRange.value.value = (100 - currentActiveObject.value.opacity! * 100).toString();
        opacitySlider.value = 100 - currentActiveObject.value.opacity! * 100;
      }

      if (borderElement.value) {
        borderElement.value.style.background = currentActiveObject.value.stroke!;
      }
    });

    const pxToInches = (value: number) => {
      return value / dpi.value;
    };

    const calculateDimsAndRes = () => {
      const width = currentActiveObject.value.width * currentActiveObject.value.scaleX!;
      const height = currentActiveObject.value.height * currentActiveObject.value.scaleY!;

      imageWidth.value = Number(pxToInches(width).toFixed(2));
      imageHeight.value = Number(pxToInches(height).toFixed(2));

      resolution.value = Math.round(currentActiveObject.value.width / imageWidth.value);
      resolutionWidth.value = Math.round(currentActiveObject.value.width / imageWidth.value);
      resolutionHeight.value = Math.round(currentActiveObject.value.height / imageHeight.value);
    };

    const handleWidth = (e: { target: HTMLInputElement }) => {
      const value = Number(e.target.value);

      if (!Number.isNaN(value) && value > 0) {
        const widthInPx = value * dpi.value;
        const currentWidth = currentActiveObject.value.width;
        const scaleX = widthInPx / currentWidth;
        updateImage("scaleX", scaleX);

        resolution.value = Math.round(currentActiveObject.value.width / imageWidth.value);
        resolutionWidth.value = Math.round(currentActiveObject.value.width / imageWidth.value);
      }
    };

    const handleHeight = (e: { target: HTMLInputElement }) => {
      const value = Number(e.target.value);

      if (!Number.isNaN(value) && value > 0) {
        const heightInPx = value * dpi.value;
        const currentHeight = currentActiveObject.value.height;
        const scaleY = heightInPx / currentHeight;
        updateImage("scaleY", scaleY);

        resolutionHeight.value = Math.round(currentActiveObject.value.height / imageHeight.value);
      }
    };

    const resetDPI = async () => {
      await updateImage("scaleX", scaledDPI);
      await updateImage("scaleY", scaledDPI);
      await nextTick();

      calculateDimsAndRes();
    };

    const handleBorderWidth = (e: { target: HTMLInputElement }): void => {
      borderSlider.value = Number(e.target.value) / 10;

      if (currentActiveObject.value) {
        updateImage("strokeWidth", borderSlider.value);
      }
    };

    const handleOpacity = (e: { target: HTMLInputElement }): void => {
      opacitySlider.value = Number(e.target.value);

      if (currentActiveObject.value) {
        updateImage("opacity", 1 - opacitySlider.value / 100);
      }
    };

    const handleToggleColorPickr = (e: { target: HTMLDivElement }): void => {
      const TARGET = e.target;

      switch (TARGET.getAttribute("id")) {
        case "border-circle": {
          let payload = {
            ...colorPickrState.value,
            isOpen: false,
            id: TARGET.getAttribute("id")!,
            property: {
              name: "stroke",
              value: TARGET.style.backgroundColor,
            },
          };

          usePickr(payload);
          break;
        }
        default: {
          throw new Error("Missing target id!, Customize.vue");
        }
      }
    };

    const prevLabel = ref<HTMLLabelElement>();
    onMounted(() => {
      const strokeDashArray = currentActiveObject.value.strokeDashArray
        ? currentActiveObject.value.strokeDashArray[0]
        : null;

      switch (strokeDashArray) {
        case 2: {
          const borderStyleInput = document.getElementById("dotted");

          if (borderStyleInput) {
            const label = borderStyleInput.parentNode as HTMLLabelElement;
            label.className = "option-selected";

            prevLabel.value = label;
          }
          break;
        }
        case 6: {
          const borderStyleInput = document.getElementById("dashed");

          if (borderStyleInput) {
            const label = borderStyleInput.parentNode as HTMLLabelElement;
            label.className = "option-selected";

            prevLabel.value = label;
          }
          break;
        }
        default: {
          const borderStyleInput = document.getElementById("solid");

          if (borderStyleInput) {
            const label = borderStyleInput.parentNode as HTMLLabelElement;
            label.className = "option-selected";

            prevLabel.value = label;
          }

          break;
        }
      }
    });

    const setBorderStyle = (e: { target: HTMLInputElement }) => {
      const value = e.target.value;

      switch (value) {
        case "solid": {
          updateImage("strokeDashArray", undefined);
          break;
        }
        case "dotted": {
          updateImage("strokeDashArray", [2, 5]);
          break;
        }
        case "dashed": {
          updateImage("strokeDashArray", [6, 5]);
          break;
        }
      }

      const label = e.target.parentNode as HTMLLabelElement;

      if (prevLabel.value) {
        prevLabel.value.className = "";
      }

      if (label) {
        label.className = "option-selected";
        prevLabel.value = label;
      }
    };

    return {
      currentActiveObject,
      imageWidth,
      imageHeight,
      resolution,
      resolutionWidth,
      resolutionHeight,
      borderSlider,
      opacitySlider,
      borderWidthRange,
      opacityRange,
      borderElement,
      handleWidth,
      handleHeight,
      resetDPI,
      handleBorderWidth,
      handleOpacity,
      handleToggleColorPickr,
      setBorderStyle,
    };
  },
});
</script>

<style scoped>
#edit-image-component {
  width: 90%;
  margin: 20px auto 0 auto;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

#image-size-wrap {
  width: 100%;
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-bottom: 0.5px solid #fff;
}

.title {
  font-size: 18px;
  font-weight: 700;
}

#inputs-wrap {
  display: flex;
  justify-content: space-between;
}

.input-container {
  display: flex;
  flex-direction: column;
  width: 45%;
  border-radius: 4px;
}

.input-container input {
  margin-top: 5px;
  height: 25px;
  padding-left: 10px;
}

.input-left {
  width: 80%;
  border-right: none;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.input-left:focus {
  outline: none;
}

input[name="right-input"] {
  pointer-events: none;
}

.input-right {
  width: 20%;
  border-left: none;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

#reset-btn {
  width: 35%;
  background: transparent;
  color: white;
  text-align: center;
  box-shadow: 1px 1px 1px #777;
  padding: 2px 6px;
  font-weight: 400;
  border-radius: 4px;
  border: solid 2px #fff;
  text-shadow: none;
  margin: 0;
  margin-bottom: 10px;
}

#reset-btn:hover {
  background: #fff;
  color: #424753;
}

#border-wrap {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 225px;
  margin-top: 10px;
  border-bottom: 0.5px solid #fff;
}

#border-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100px;
}

#border-controls p {
  margin-bottom: 5px;
}

#color-wrap p {
  text-align: center;
}

#border-circle {
  width: 50px;
  height: 50px;
  background: #0001ba;
  border: 3px solid white;
  border-radius: 50%;
}

#styles-wrap {
  display: flex;
  flex-direction: column;
  margin: 10px 0;
}

#border-styles {
  margin-top: 10px;
  display: flex;
}

#border-styles label {
  border: 1px solid white;
  width: 75px;
  text-align: center;
  padding: 5px;
  border-radius: 4px;
  margin: 2px;
  display: flex;
  justify-content: center;
}

#border-styles label:hover {
  background: #fff;
  color: #424753;
}

#border-styles input {
  display: none;
}

.option-selected {
  background: #fff;
  color: #424753;
}

#opacity-controls {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 125px;
  border-bottom: 0.5px solid #fff;
  margin-top: 10px;
}

#slider-wrap {
  display: flex;
  height: 60%;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
}

#slider-wrap p {
  width: 100%;
}

#slider-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

#opacity-slider {
  width: 90%;
}

button {
  width: 90%;
  border: none;
  border-radius: 2px;
  background: #ff6c00;
  color: #fff;
  padding: 15px 6px;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  margin-top: 20px;
  margin-bottom: 20px;
  cursor: pointer;
}

button:hover {
  background: linear-gradient(to bottom, #ff994e 0%, #ff6c00 100%);
}
</style>
